import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';
import { mockSave } from '../services/botConfigService.js';

const TOPICS = [
  { id: 'violence', label: 'Violence and physical harm', description: 'Instructions or encouragement to hurt people.' },
  { id: 'self-harm', label: 'Self-harm', description: 'Encouragement or instructions for self-injury.' },
  { id: 'hate', label: 'Hate and harassment', description: 'Abusive or hateful content targeting people.' },
  { id: 'sexual', label: 'Explicit sexual content', description: 'Graphic or sexually explicit conversations.' },
  { id: 'illegal', label: 'Illegal activities', description: 'Help planning or carrying out unlawful activity.' },
  { id: 'personal-data', label: 'Sensitive personal data', description: 'Requests to expose private or confidential information.' },
  { id: 'medical', label: 'Medical diagnosis or treatment', description: 'Requests for a diagnosis or personalized treatment.' },
  { id: 'financial', label: 'Personal financial advice', description: 'Requests for individualized investment or financial decisions.' },
];
const MAX_CUSTOM = 20;
const MAX_TOPIC_LENGTH = 80;

export function ProhibitedTopicsPanel({ bot, updateBot, mockErrorOn, notify }) {
  const [selected, setSelected] = useState(bot.prohibitedTopics || []);
  const [custom, setCustom] = useState(bot.customProhibitedTopics || []);
  const [draft, setDraft] = useState('');
  const [action, setAction] = useState(bot.prohibitedTopicAction || 'fallback');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const dirty = JSON.stringify(selected) !== JSON.stringify(bot.prohibitedTopics || []) ||
    JSON.stringify(custom) !== JSON.stringify(bot.customProhibitedTopics || []) || action !== (bot.prohibitedTopicAction || 'fallback');
  const empty = selected.length === 0 && custom.length === 0;

  function toggle(id) {
    setSelected((current) => current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
    setError('');
  }
  function addCustom() {
    const value = draft.trim();
    if (!value) { setError('Enter a topic before adding it.'); return; }
    if (value.length > MAX_TOPIC_LENGTH) { setError(`Custom topics must be ${MAX_TOPIC_LENGTH} characters or fewer.`); return; }
    if (custom.some((x) => x.toLowerCase() === value.toLowerCase()) || TOPICS.some((x) => x.label.toLowerCase() === value.toLowerCase())) {
      setError('This topic is already included.'); return;
    }
    if (custom.length >= MAX_CUSTOM) { setError(`You can add up to ${MAX_CUSTOM} custom topics.`); return; }
    setCustom((items) => [...items, value]); setDraft(''); setError('');
  }
  function removeCustom(value) { setCustom((items) => items.filter((x) => x !== value)); }
  function save() {
    if (selected.length + custom.length === 0) { setError('Select at least one prohibited topic.'); return; }
    if (draft.trim()) { setError('Add or clear your custom topic before saving.'); return; }
    setError(''); setStatus('saving');
    mockSave(mockErrorOn).then(() => {
      updateBot({ prohibitedTopics: selected, customProhibitedTopics: custom, prohibitedTopicAction: action });
      setStatus('idle'); notify('success', 'Prohibited-topic settings saved');
    }).catch(() => setStatus('error'));
  }
  return <div>
    <div className="panel-header">
      <h1>Prohibited-topic configuration</h1>
      <p>Choose topics your bot should not engage with and decide how it should respond when a visitor raises one.</p>
    </div>
    {status === 'error' && <AlertBanner text="We couldn't save your changes. Try again." onRetry={save} />}
    <div className="card">
      {empty && <div className="empty-state" style={{ paddingTop: 8 }} role="status" aria-live="polite">
        <div className="empty-icon"><Icon.alert s={22} /></div>
        <h3>No prohibited topics selected</h3><p>Select at least one topic below. You can also add a custom topic before saving.</p>
      </div>}
      <div className="field">
        <label>Prohibited topics <span className="counter">{selected.length + custom.length} selected</span></label>
        <div className="row g-3">
          {TOPICS.map((topic) => <div className="col-12 col-md-6" key={topic.id}>
            <label className="prohibited-topic-option">
              <input type="checkbox" checked={selected.includes(topic.id)} onChange={() => toggle(topic.id)} disabled={status === 'saving'} />
              <span><strong>{topic.label}</strong><small>{topic.description}</small></span>
            </label>
          </div>)}
        </div>
      </div>
      <div className="field">
        <label htmlFor="custom-topic">Add a custom topic <span className="counter">{custom.length} / {MAX_CUSTOM}</span></label>
        <div className="d-flex gap-2">
          <input id="custom-topic" type="text" value={draft} maxLength={MAX_TOPIC_LENGTH + 1} placeholder="e.g. Competitor pricing" onChange={(e) => { setDraft(e.target.value); setError(''); }} disabled={status === 'saving'} />
          <button type="button" className="btn-secondary" onClick={addCustom} disabled={status === 'saving'}>Add</button>
        </div>
        <div className="hint">Custom topics are limited to {MAX_TOPIC_LENGTH} characters each.</div>
        {custom.length > 0 && <div className="chip-row">{custom.map((topic) => <span className="chip" key={topic}>{topic}<button type="button" aria-label={`Remove ${topic}`} onClick={() => removeCustom(topic)} disabled={status === 'saving'}>×</button></span>)}</div>}
      </div>
      <div className="field">
        <label htmlFor="prohibited-action">When a prohibited topic is detected</label>
        <select id="prohibited-action" value={action} onChange={(e) => setAction(e.target.value)} disabled={status === 'saving'}>
          <option value="fallback">Show the configured fallback message</option>
          <option value="handoff">Offer a human handoff</option>
          <option value="decline">Politely decline and redirect the conversation</option>
        </select>
        <div className="hint">This is a frontend prototype. The selected response action is stored in the local mock configuration.</div>
      </div>
      {error && <div className="error-text" role="alert" aria-live="assertive"><Icon.alert s={12} /> {error}</div>}
      <div className="actions-row">
        <button className="btn-primary" type="button" onClick={save} disabled={status === 'saving' || (!dirty && !empty) || !!draft.trim()}>
          {status === 'saving' && <span className="spinner" />}{status === 'saving' ? 'Saving…' : 'Save changes'}
        </button>
        <button className="btn-secondary" type="button" onClick={() => { setSelected(bot.prohibitedTopics || []); setCustom(bot.customProhibitedTopics || []); setAction(bot.prohibitedTopicAction || 'fallback'); setDraft(''); setError(''); }} disabled={status === 'saving' || !dirty}>Reset</button>
      </div>
    </div>
  </div>;
}
